import { getStoredReferral } from '@/lib/referral';
import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DiscountCodeInput, type DiscountResult } from "@/components/DiscountCodeInput";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useProgramBySlug, usePublicCohorts } from "@/hooks/usePrograms";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  ArrowLeft,
  ArrowRight,
  Mail,
  User,
  Tag,
  CreditCard,
  CheckCircle2,
  Loader2,
  ShieldCheck,
} from "lucide-react";

const EnrollmentPage = () => {
  const { slug, cohortId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: program, isLoading: programLoading } = useProgramBySlug(slug);
  const { data: cohorts } = usePublicCohorts(program?.id);
  const cohort = (cohorts || []).find((c) => c.id === cohortId);

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState(user?.email || "");
  const [fullName, setFullName] = useState(
    user?.user_metadata?.full_name || ""
  );
  const [discount, setDiscount] = useState<DiscountResult | null>(null);
  const [loading, setLoading] = useState(false);

  const finalPrice = discount ? discount.final_price : (cohort?.price ?? 0);

  const handleNext = () => {
    if (step === 1) {
      if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        toast.error("Ingresa un correo electrónico válido");
        return;
      }
      if (!fullName.trim()) {
        toast.error("Ingresa tu nombre completo");
        return;
      }
      setStep(2);
    }
  };

  const handlePay = async () => {
    if (!cohort) return;
    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke(
        "create-checkout-session",
        {
          body: {
            email: email.trim(),
            name: fullName.trim(),
            cohort_id: cohort.id,
            discount_code_id: discount?.code_id || null,
            referral_code: getStoredReferral(),
            success_url: `${window.location.origin}/inscripcion/resultado?session_id={CHECKOUT_SESSION_ID}`,
            cancel_url: window.location.href,
          },
        }
      );

      if (error) throw error;

      if (data.free) {
        // 100% discount — redirect to result page
        navigate(`/inscripcion/resultado?free=true&email=${encodeURIComponent(email)}`);
        return;
      }

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "Error al procesar el pago");
    } finally {
      setLoading(false);
    }
  };

  if (programLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Self-paced (perpetual) routes are not enrolled through this cohort checkout flow
  if (cohort?.is_perpetual || (program as any)?.modality === "self_paced") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 gap-4 text-center">
        <h1 className="text-2xl font-bold text-foreground">Ruta de autoaprendizaje</h1>
        <p className="text-muted-foreground max-w-md">
          Esta ruta no requiere inscripción a una cohorte: se accede con una licencia activa y avanzas a tu propio ritmo.
        </p>
        <Button onClick={() => navigate(`/rutas/${slug}`)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Ver la ruta
        </Button>
      </div>
    );
  }

  if (!program || !cohort) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 gap-4">
        <h1 className="text-2xl font-bold text-foreground">Cohorte no encontrada</h1>
        <Button onClick={() => navigate("/rutas")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-muted/30 to-background">
      <div className="max-w-2xl mx-auto px-4 py-8 md:py-16">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/rutas/${slug}`)}
            className="gap-2 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver a la ruta
          </Button>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">
            Inscripción
          </h1>
          <p className="text-muted-foreground mt-1">{program.name}</p>
          <Badge variant="secondary" className="mt-2">
            {cohort.name}
            {cohort.start_date && ` — Inicia ${format(new Date(cohort.start_date), "d MMM yyyy", { locale: es })}`}
          </Badge>
        </div>

        {/* Steps indicator */}
        <div className="flex items-center gap-2 mb-8">
          {[
            { n: 1, icon: User, label: "Datos" },
            { n: 2, icon: Tag, label: "Descuento" },
            { n: 3, icon: CreditCard, label: "Pago" },
          ].map(({ n, icon: Icon, label }) => (
            <div key={n} className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                  step >= n
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {step > n ? <CheckCircle2 className="w-4 h-4" /> : n}
              </div>
              <span className={`text-sm hidden sm:inline ${step >= n ? "text-foreground font-medium" : "text-muted-foreground"}`}>
                {label}
              </span>
              {n < 3 && <div className="w-8 h-px bg-border" />}
            </div>
          ))}
        </div>

        {/* Step 1: Email + Name */}
        {step === 1 && (
          <Card>
            <CardContent className="p-6 space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="fullName">Nombre completo</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="fullName"
                      placeholder="Tu nombre completo"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="pl-9"
                      disabled={!!user}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Correo electrónico</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="tu@correo.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-9"
                      disabled={!!user}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Tu acceso a la ruta se asociará a este correo
                  </p>
                </div>
              </div>

              <Button onClick={handleNext} className="w-full gap-2">
                Continuar
                <ArrowRight className="w-4 h-4" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Discount code */}
        {step === 2 && (
          <Card>
            <CardContent className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-1">
                  ¿Tienes un código de descuento?
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Si tienes un código promocional, ingrésalo aquí. Si no, continúa al pago.
                </p>
                <DiscountCodeInput
                  originalPrice={cohort.price ?? 0}
                  context="cohort"
                  cohortId={cohort.id}
                  onApply={setDiscount}
                />
              </div>

              {/* Price summary */}
              <div className="bg-muted/50 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Precio de la ruta</span>
                  <span className="text-foreground">
                    {cohort.currency || "USD"} ${cohort.price}
                  </span>
                </div>
                {discount && (
                  <div className="flex justify-between text-sm text-emerald-600">
                    <span>Descuento ({discount.discount_type === "percentage" ? `${discount.discount_value}%` : `$${discount.discount_value}`})</span>
                    <span>-${discount.calculated_discount}</span>
                  </div>
                )}
                <div className="border-t border-border pt-2 flex justify-between font-semibold">
                  <span className="text-foreground">Total a pagar</span>
                  <span className="text-foreground text-lg">
                    {cohort.currency || "USD"} ${finalPrice}
                  </span>
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(1)} className="gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Atrás
                </Button>
                <Button onClick={() => setStep(3)} className="flex-1 gap-2">
                  Continuar al pago
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Payment summary + pay */}
        {step === 3 && (
          <Card>
            <CardContent className="p-6 space-y-6">
              <h3 className="text-lg font-semibold text-foreground">
                Confirmar inscripción
              </h3>

              <div className="bg-muted/50 rounded-xl p-4 space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Ruta</span>
                  <span className="text-foreground font-medium">{program.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Cohorte</span>
                  <span className="text-foreground">{cohort.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Nombre</span>
                  <span className="text-foreground">{fullName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Correo</span>
                  <span className="text-foreground">{email}</span>
                </div>
                {discount && (
                  <div className="flex justify-between text-emerald-600">
                    <span>Descuento</span>
                    <span>-${discount.calculated_discount}</span>
                  </div>
                )}
                <div className="border-t border-border pt-2 flex justify-between font-semibold text-base">
                  <span className="text-foreground">Total</span>
                  <span className="text-foreground">
                    {cohort.currency || "USD"} ${finalPrice}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <ShieldCheck className="w-4 h-4 text-primary flex-shrink-0" />
                Pago seguro procesado por Stripe. Tu información está protegida.
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(2)} className="gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Atrás
                </Button>
                <Button
                  onClick={handlePay}
                  disabled={loading}
                  className="flex-1 gap-2"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CreditCard className="w-4 h-4" />
                  )}
                  {finalPrice <= 0 ? "Inscribirme gratis" : `Pagar ${cohort.currency || "USD"} $${finalPrice}`}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default EnrollmentPage;
